<template>
    <Transition name="admin2026-drawer-transition">
        <div v-if="open"
             class="fixed inset-0 z-[80] overflow-hidden"
             role="dialog"
             aria-modal="true"
             aria-labelledby="admin2026-training-plan-quick-preview-title"
             data-admin2026-training-plan-quick-preview-drawer="true"
             @keydown.escape.window="handleEscape">
            <div class="absolute inset-0 z-0 cursor-pointer admin2026-drawer-backdrop"
                 aria-hidden="true"
                 @click="close"></div>

            <div class="pointer-events-none absolute inset-y-0 right-0 flex max-w-full admin2026-drawer-rail">
                <section ref="drawer"
                         class="pointer-events-auto relative z-10 flex h-full w-screen flex-col overflow-hidden border-l border-slate-200 bg-white shadow-2xl admin2026-drawer-panel dark:border-slate-800 dark:bg-slate-950"
                         style="width: min(100vw, 96rem);"
                         tabindex="-1"
                         data-admin2026-drawer-focus-target="true"
                         data-admin2026-training-plan-quick-preview-panel="true"
                         @click.stop>
                    <header class="shrink-0 border-b border-slate-200 bg-white px-5 py-4 dark:border-slate-800 dark:bg-slate-950 sm:px-7">
                        <div class="flex items-start justify-between gap-4">
                            <div class="flex min-w-0 items-start gap-3">
                                <span class="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-200">
                                    <i class="fa-solid fa-eye" aria-hidden="true"></i>
                                </span>
                                <div class="min-w-0">
                                    <h2 id="admin2026-training-plan-quick-preview-title" class="text-xl font-semibold text-slate-950 dark:text-white">At a glance</h2>
                                    <p class="mt-1 truncate text-sm text-slate-500 dark:text-slate-400" v-text="planName"></p>
                                </div>
                            </div>

                            <div class="flex shrink-0 items-center gap-2">
                                <button type="button"
                                        ref="printButton"
                                        :class="buttonClasses"
                                        :disabled="!canPrint"
                                        @click="openPrintOptions"
                                        data-admin2026-training-plan-quick-preview-print="true">
                                    <i class="fa-solid fa-print text-sm" aria-hidden="true"></i>
                                    Print
                                </button>
                                <button type="button"
                                        :class="iconButtonClasses"
                                        @click="close"
                                        aria-label="Close At a glance"
                                        data-admin2026-training-plan-quick-preview-close="true">
                                    <i class="fa-solid fa-xmark" aria-hidden="true"></i>
                                </button>
                            </div>
                        </div>
                    </header>

                    <div class="min-h-0 flex-1 overflow-y-auto bg-slate-100 p-3 dark:bg-slate-900 sm:p-5">
                        <div v-if="loading"
                             class="mb-3 flex items-center gap-2 border border-sky-200 bg-sky-50 px-3 py-2 text-sm font-semibold text-sky-800 dark:border-sky-500/30 dark:bg-sky-500/10 dark:text-sky-100"
                             data-admin2026-training-plan-quick-preview-loading="true">
                            <i class="fa-solid fa-spinner fa-spin" aria-hidden="true"></i>
                            Loading every exercise…
                        </div>

                        <div v-if="loadError"
                             class="mb-3 flex flex-wrap items-center justify-between gap-3 border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-100"
                             role="alert"
                             data-admin2026-training-plan-quick-preview-error="true">
                            <span v-text="loadError"></span>
                            <button type="button" :class="[buttonClasses, 'px-3 py-1.5 text-xs']" @click="load">
                                <i class="fa-solid fa-rotate" aria-hidden="true"></i>
                                Try again
                            </button>
                        </div>

                        <main ref="printSurface"
                              class="admin2026-training-plan-quick-preview-sheet mx-auto bg-white text-slate-950 shadow-sm"
                              data-admin2026-training-plan-quick-preview-print-surface="true">
                            <div class="flex items-start justify-between gap-4 border-b-2 border-slate-900 px-3 py-2">
                                <div class="min-w-0">
                                    <h1 class="truncate text-lg font-bold leading-tight" v-text="planName"></h1>
                                    <p class="mt-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">Compact plan overview</p>
                                </div>
                                <div class="flex shrink-0 flex-wrap items-center justify-end gap-x-2 gap-y-1 text-[10px] font-semibold text-slate-600">
                                    <span>{{ workoutcycles.length }} cycles</span>
                                    <span aria-hidden="true">·</span>
                                    <span>{{ workouts.length }} workouts</span>
                                    <span aria-hidden="true">·</span>
                                    <span>{{ exerciseTotal }} exercises</span>
                                    <template v-if="performanceEnabled">
                                        <span aria-hidden="true">·</span>
                                        <span>{{ scheduleSummaries.length }} {{ scheduleSummaries.length === 1 ? 'schedule' : 'schedules' }}</span>
                                        <span aria-hidden="true">·</span>
                                        <span>{{ timelineDurationDays }} days</span>
                                    </template>
                                </div>
                            </div>

                            <div v-if="planDetails.length"
                                 class="grid grid-cols-1 gap-px border-b border-slate-300 bg-slate-300 sm:grid-cols-2"
                                 data-admin2026-training-plan-quick-preview-plan-details="true"
                                 data-admin2026-training-plan-quick-preview-description="true">
                                <div v-for="detail in planDetails" :key="'quick-preview-plan-detail-'+detail.key" class="min-w-0 bg-slate-50 px-3 py-1.5">
                                    <div class="text-[9px] font-bold uppercase tracking-wide text-slate-500" v-text="detail.label"></div>
                                    <TrainingPlanQuickPreviewNote :value="detail.value" :maximum-length="180" text-classes="text-[10px] leading-4 text-slate-700" />
                                </div>
                            </div>

                            <section v-if="performanceEnabled"
                                     class="border-b border-slate-300 bg-slate-50 px-3 py-2"
                                     data-admin2026-training-plan-quick-preview-flexible-plan="true">
                                <div class="flex flex-wrap items-center gap-x-3 gap-y-1">
                                    <h2 class="text-[10px] font-bold uppercase tracking-wide text-slate-700">Flexible plan options</h2>
                                    <span class="text-[9px] font-semibold text-slate-500">{{ timelineDurationDays }} total days</span>
                                    <span class="text-[9px] font-semibold text-slate-500">{{ checkpoints.length }} {{ checkpoints.length === 1 ? 'checkpoint' : 'checkpoints' }}</span>
                                </div>

                                <div v-if="scheduleSummaries.length"
                                     class="mt-2 grid gap-2 sm:grid-cols-2 xl:grid-cols-3"
                                     data-admin2026-training-plan-quick-preview-schedules="true">
                                    <article v-for="schedule in scheduleSummaries"
                                             :key="'quick-preview-schedule-'+String(schedule.key)"
                                             class="min-w-0 border border-slate-300 bg-white px-2.5 py-2 shadow-sm"
                                             data-admin2026-training-plan-quick-preview-schedule="true">
                                        <div class="flex min-w-0 items-center gap-1.5">
                                            <i class="fa-solid fa-chart-line shrink-0 text-[9px] text-slate-500" aria-hidden="true"></i>
                                            <h3 class="min-w-0 truncate text-[10px] font-bold" v-text="schedule.label"></h3>
                                            <span class="ml-auto shrink-0 text-[8px] font-semibold text-slate-500">
                                                {{ schedule.workouts.length }} {{ schedule.workouts.length === 1 ? 'workout' : 'workouts' }}
                                            </span>
                                        </div>
                                        <p v-if="plainText(schedule.content)"
                                           class="mt-0.5 truncate text-[9px] leading-3 text-slate-600"
                                           data-admin2026-training-plan-quick-preview-description="true"
                                           v-text="plainText(schedule.content)"></p>
                                        <p class="mt-0.5 truncate text-[9px] leading-3 text-slate-600"
                                           v-text="schedule.workouts.length ? schedule.workouts.map(workout => workout.name || 'Untitled workout').join(' · ') : 'No workouts included'"></p>
                                    </article>
                                </div>
                            </section>

                            <div v-if="groups.length || (performanceEnabled && checkpoints.length)"
                                 class="admin2026-training-plan-quick-preview-cycles p-2"
                                 data-admin2026-training-plan-quick-preview-cycles="true">
                                <div v-if="performanceEnabled"
                                     class="admin2026-training-plan-quick-preview-timeline-heading mb-2 flex items-center justify-between gap-3 border-b border-slate-300 pb-1">
                                    <h2 class="text-[10px] font-bold uppercase tracking-wide text-slate-700">Plan timeline</h2>
                                    <span class="text-[9px] font-semibold text-slate-500">{{ timelineDurationDays }} days</span>
                                </div>

                                <div v-if="performanceEnabled && planStartCheckpoints.length"
                                     class="mb-2 space-y-1 border-l-2 border-amber-400 pl-2"
                                     data-admin2026-training-plan-quick-preview-plan-start-checkpoints="true">
                                    <p class="text-[9px] font-bold uppercase tracking-wide text-amber-800">Before the plan starts</p>
                                    <TrainingPlanQuickPreviewCheckpoint v-for="checkpoint in planStartCheckpoints"
                                                                       :key="'quick-preview-plan-start-checkpoint-'+String(checkpoint.id || checkpoint.client_key)"
                                                                       :checkpoint="checkpoint"
                                                                       :timing-label="checkpointTimingLabel(checkpoint)"
                                                                       :type-label="checkpointTypeLabel(checkpoint)"
                                                                       :response-types="responseTypes" />
                                </div>

                                <section v-for="(group, groupIndex) in groups"
                                         :key="'quick-preview-group-'+String(group.id)+'-'+groupIndex"
                                         class="admin2026-training-plan-quick-preview-cycle mb-2 border border-slate-400 bg-white last:mb-0"
                                         data-admin2026-training-plan-quick-preview-cycle="true">
                                    <header class="border-b border-slate-400 bg-slate-900 px-2 py-1 text-white">
                                        <div class="flex min-w-0 items-baseline gap-2">
                                            <span class="text-[9px] font-bold uppercase tracking-wide text-slate-300" v-text="group.unassigned ? 'Other' : 'Cycle ' + (groupIndex + 1)"></span>
                                            <h2 class="min-w-0 truncate text-xs font-bold" v-text="group.name"></h2>
                                            <span class="ml-auto shrink-0 text-[9px] font-semibold text-slate-300">{{ group.workouts.length }} {{ group.workouts.length === 1 ? 'workout' : 'workouts' }}</span>
                                        </div>
                                        <div v-if="performanceEnabled && !group.unassigned"
                                             class="mt-0.5 flex min-w-0 items-baseline gap-2 text-[9px] leading-3 text-slate-200"
                                             data-admin2026-training-plan-quick-preview-cycle-timeline="true">
                                            <span class="shrink-0 font-bold text-slate-300" v-text="timelineRange(group)"></span>
                                            <template v-if="group.purpose">
                                                <span aria-hidden="true">·</span>
                                                <span class="shrink-0 font-bold text-slate-300">Client focus:</span>
                                                <span class="truncate" v-text="group.purpose"></span>
                                            </template>
                                        </div>
                                        <div v-if="plainText(group.content)"
                                             class="mt-0.5"
                                             data-admin2026-training-plan-quick-preview-description="true">
                                            <TrainingPlanQuickPreviewNote :value="group.content" :maximum-length="180" text-classes="text-[9px] leading-3 text-slate-200" />
                                        </div>
                                        <div v-if="plainText(group.cycle_focus)"
                                             class="mt-0.5 flex min-w-0 items-baseline gap-1 text-[9px] leading-3 text-slate-200"
                                             data-admin2026-training-plan-quick-preview-description="true">
                                            <span class="shrink-0 font-bold text-slate-300">Focus:</span>
                                            <span class="truncate" v-text="plainText(group.cycle_focus).replace(/\n/g, ' · ')"></span>
                                        </div>
                                    </header>

                                    <div v-if="performanceEnabled && !group.unassigned && cycleCheckpoints(group.id, 'cycle_start').length"
                                         class="space-y-1 border-b border-slate-300 bg-amber-50/60 px-2 py-1.5"
                                         data-admin2026-training-plan-quick-preview-cycle-start-checkpoints="true">
                                        <TrainingPlanQuickPreviewCheckpoint v-for="checkpoint in cycleCheckpoints(group.id, 'cycle_start')"
                                                                           :key="'quick-preview-cycle-start-checkpoint-'+String(checkpoint.id || checkpoint.client_key)"
                                                                           :checkpoint="checkpoint"
                                                                           :timing-label="checkpointTimingLabel(checkpoint)"
                                                                           :type-label="checkpointTypeLabel(checkpoint)"
                                                                           :response-types="responseTypes" />
                                    </div>

                                    <div v-if="group.workouts.length" class="divide-y divide-slate-400">
                                        <article v-for="(workout, workoutIndex) in group.workouts"
                                                 :key="'quick-preview-workout-'+String(workout.id)+'-'+workoutIndex"
                                                 class="admin2026-training-plan-quick-preview-workout"
                                                 data-admin2026-training-plan-quick-preview-workout="true">
                                            <div class="flex min-w-0 items-baseline gap-2 bg-slate-100 px-2 py-1">
                                                <h3 class="min-w-0 truncate text-[10px] font-bold" v-text="workoutLabel(workout, workoutIndex)"></h3>
                                                <span v-if="workoutDay(workout)" class="shrink-0 text-[9px] font-semibold text-slate-500" v-text="workoutDay(workout)"></span>
                                                <span v-if="performanceEnabled && scheduleLabelsForWorkout(workout.id).length"
                                                      class="min-w-0 truncate bg-slate-200 px-1.5 py-0.5 text-[8px] font-semibold text-slate-700"
                                                      data-admin2026-training-plan-quick-preview-workout-schedules="true">
                                                    <i class="fa-solid fa-chart-line mr-0.5" aria-hidden="true"></i>
                                                    {{ scheduleLabelsForWorkout(workout.id).join(' · ') }}
                                                </span>
                                                <span class="ml-auto shrink-0 text-[9px] text-slate-500">{{ exerciseCount(workout) }} exercises</span>
                                            </div>
                                            <div v-if="plainText(workout.content)"
                                                 class="border-t border-slate-200 px-2 py-1"
                                                 data-admin2026-training-plan-quick-preview-workout-notes="true"
                                                 data-admin2026-training-plan-quick-preview-description="true">
                                                <TrainingPlanQuickPreviewNote :value="workout.content" :maximum-length="180" text-classes="text-[9px] leading-3 text-slate-600" />
                                            </div>

                                            <div v-if="workoutItemsLoading(workout)" class="border-t border-slate-200 px-2 py-2 text-center text-[9px] text-slate-500">Loading exercises…</div>
                                            <div v-else-if="workoutItemsError(workout)" class="border-t border-rose-200 bg-rose-50 px-2 py-2 text-[9px] text-rose-700" v-text="workoutItemsError(workout)"></div>
                                            <div v-else-if="workoutItemsLoaded(workout) && (!workout.workoutitems || !workout.workoutitems.length)" class="border-t border-slate-200 px-2 py-2 text-center text-[9px] text-slate-500">No exercises.</div>
                                            <table v-else-if="workoutItemsLoaded(workout)" class="w-full table-fixed border-collapse text-left text-[9px] leading-3" data-admin2026-training-plan-quick-preview-exercise-table="true">
                                                <colgroup>
                                                    <col class="w-6">
                                                    <col class="w-[27%]">
                                                    <col class="w-9">
                                                    <col class="w-[34%]">
                                                    <col data-admin2026-training-plan-quick-preview-notes-column="true">
                                                </colgroup>
                                                <thead>
                                                    <tr class="border-y border-slate-300 bg-slate-50 text-[8px] font-bold uppercase tracking-wide text-slate-500">
                                                        <th class="px-1 py-0.5 text-center">#</th>
                                                        <th class="px-1 py-0.5">Exercise</th>
                                                        <th class="px-1 py-0.5 text-center">Sets</th>
                                                        <th class="px-1 py-0.5">Variables</th>
                                                        <th class="px-1 py-0.5" data-admin2026-training-plan-quick-preview-notes-column="true">Notes</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <template v-for="(item, itemIndex) in workout.workoutitems" :key="'quick-preview-item-'+String(item.id)+'-'+itemIndex">
                                                        <tr v-if="item.new_section && (item.section_name || plainText(item.section_content))"
                                                            class="border-t border-slate-300 bg-slate-200"
                                                            data-admin2026-training-plan-quick-preview-section="true">
                                                            <td colspan="5" class="px-1.5 py-0.5">
                                                                <div class="flex min-w-0 items-baseline gap-1.5">
                                                                    <span class="shrink-0 text-[8px] font-bold uppercase tracking-wide text-slate-500">Section</span>
                                                                    <span v-if="item.section_name"
                                                                          class="shrink-0 font-bold"
                                                                          data-admin2026-training-plan-quick-preview-section-name="true"
                                                                          v-text="item.section_name"></span>
                                                                    <div v-if="plainText(item.section_content)"
                                                                         class="min-w-0 flex-1"
                                                                         data-admin2026-training-plan-quick-preview-description="true">
                                                                        <TrainingPlanQuickPreviewNote :value="item.section_content" :maximum-length="150" text-classes="text-[9px] leading-3 text-slate-600" />
                                                                    </div>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                        <tr class="border-t border-slate-200 align-top" data-admin2026-training-plan-quick-preview-exercise="true">
                                                            <td class="px-1 py-0.5 text-center text-slate-400" v-text="itemIndex + 1"></td>
                                                            <td class="truncate px-1 py-0.5 font-semibold" v-text="item.name || 'Unnamed exercise'"></td>
                                                            <td class="px-1 py-0.5 text-center" v-text="parseInt(item.sets || 0, 10) > 0 ? item.sets : '—'"></td>
                                                            <td class="truncate px-1 py-0.5" v-text="variableSummary(item) || '—'"></td>
                                                            <td class="px-1 py-0.5" data-admin2026-training-plan-quick-preview-notes-column="true">
                                                                <TrainingPlanQuickPreviewNote v-if="plainText(item.content)" :value="item.content" :maximum-length="90" text-classes="text-[9px] leading-3 text-slate-600" />
                                                                <span v-else class="text-slate-400">—</span>
                                                            </td>
                                                        </tr>
                                                    </template>
                                                </tbody>
                                            </table>

                                            <div v-if="performanceEnabled && workoutCheckpoints(workout.id).length"
                                                 class="space-y-1 border-t border-slate-300 bg-amber-50/60 px-2 py-1.5"
                                                 data-admin2026-training-plan-quick-preview-workout-checkpoints="true">
                                                <TrainingPlanQuickPreviewCheckpoint v-for="checkpoint in workoutCheckpoints(workout.id)"
                                                                                   :key="'quick-preview-workout-checkpoint-'+String(checkpoint.id || checkpoint.client_key)"
                                                                                   :checkpoint="checkpoint"
                                                                                   :timing-label="checkpointTimingLabel(checkpoint)"
                                                                                   :type-label="checkpointTypeLabel(checkpoint)"
                                                                                   :response-types="responseTypes" />
                                            </div>
                                        </article>
                                    </div>
                                    <div v-else class="px-2 py-2 text-center text-[9px] text-slate-500">No workouts in this cycle.</div>

                                    <div v-if="performanceEnabled && !group.unassigned && cycleCheckpoints(group.id, 'cycle_end').length"
                                         class="space-y-1 border-t border-slate-300 bg-amber-50/60 px-2 py-1.5"
                                         data-admin2026-training-plan-quick-preview-cycle-end-checkpoints="true">
                                        <TrainingPlanQuickPreviewCheckpoint v-for="checkpoint in cycleCheckpoints(group.id, 'cycle_end')"
                                                                           :key="'quick-preview-cycle-end-checkpoint-'+String(checkpoint.id || checkpoint.client_key)"
                                                                           :checkpoint="checkpoint"
                                                                           :timing-label="checkpointTimingLabel(checkpoint)"
                                                                           :type-label="checkpointTypeLabel(checkpoint)"
                                                                           :response-types="responseTypes" />
                                    </div>
                                </section>

                                <div v-if="performanceEnabled && planEndCheckpoints.length"
                                     class="mt-2 space-y-1 border-l-2 border-amber-400 pl-2"
                                     data-admin2026-training-plan-quick-preview-plan-end-checkpoints="true">
                                    <p class="text-[9px] font-bold uppercase tracking-wide text-amber-800">At the end of the plan</p>
                                    <TrainingPlanQuickPreviewCheckpoint v-for="checkpoint in planEndCheckpoints"
                                                                       :key="'quick-preview-plan-end-checkpoint-'+String(checkpoint.id || checkpoint.client_key)"
                                                                       :checkpoint="checkpoint"
                                                                       :timing-label="checkpointTimingLabel(checkpoint)"
                                                                       :type-label="checkpointTypeLabel(checkpoint)"
                                                                       :response-types="responseTypes" />
                                </div>

                                <div v-if="performanceEnabled && unplacedCheckpoints.length"
                                     class="mt-2 border border-amber-400 bg-amber-50 px-2 py-1.5"
                                     data-admin2026-training-plan-quick-preview-unplaced-checkpoints="true">
                                    <p class="text-[9px] font-bold uppercase tracking-wide text-amber-800">Checkpoints needing a new timeline location</p>
                                    <div class="mt-1 space-y-1">
                                        <TrainingPlanQuickPreviewCheckpoint v-for="checkpoint in unplacedCheckpoints"
                                                                           :key="'quick-preview-unplaced-checkpoint-'+String(checkpoint.id || checkpoint.client_key)"
                                                                           :checkpoint="checkpoint"
                                                                           :timing-label="checkpointTimingLabel(checkpoint)"
                                                                           :type-label="checkpointTypeLabel(checkpoint)"
                                                                           :response-types="responseTypes" />
                                    </div>
                                </div>
                            </div>

                            <div v-else class="px-4 py-8 text-center text-sm text-slate-500" data-admin2026-training-plan-quick-preview-empty="true">No cycles or workouts have been added yet.</div>
                        </main>
                    </div>
                </section>
            </div>

            <div v-if="printOptionsOpen"
                 class="fixed inset-0 z-[90] flex items-center justify-center p-4"
                 role="dialog"
                 aria-modal="true"
                 aria-labelledby="admin2026-training-plan-quick-preview-print-options-title"
                 data-admin2026-training-plan-quick-preview-print-options="true"
                 @click.self="closePrintOptions">
                <div class="absolute inset-0 bg-slate-950/55 backdrop-blur-sm" aria-hidden="true" @click="closePrintOptions"></div>

                <section ref="printOptionsDialog"
                         class="relative z-10 w-full max-w-lg overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900"
                         tabindex="-1"
                         @click.stop>
                    <div class="border-b border-slate-200 px-5 py-5 dark:border-slate-800 sm:px-6">
                        <div class="flex items-start gap-3">
                            <span class="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-200">
                                <i class="fa-solid fa-print" aria-hidden="true"></i>
                            </span>
                            <div class="min-w-0">
                                <h3 id="admin2026-training-plan-quick-preview-print-options-title" class="text-lg font-semibold text-slate-950 dark:text-white">How would you like to print it?</h3>
                                <p class="mt-1 text-sm leading-6 text-slate-500 dark:text-slate-400">Choose the amount of detail that works best for this copy.</p>
                            </div>
                        </div>
                    </div>

                    <fieldset class="space-y-3 px-5 py-5 sm:px-6">
                        <legend class="sr-only">Description preference</legend>
                        <label :class="[
                                   'flex cursor-pointer items-start gap-3 rounded-2xl border p-4 transition',
                                   printDescriptions
                                       ? 'border-emerald-400 bg-emerald-50 ring-1 ring-emerald-200 dark:border-emerald-500/70 dark:bg-emerald-500/10 dark:ring-emerald-500/20'
                                       : 'border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/50 dark:border-slate-700 dark:hover:border-emerald-500/50 dark:hover:bg-emerald-500/10'
                               ]">
                            <input v-model="printDescriptions"
                                   :value="true"
                                   type="radio"
                                   name="training_plan_quick_preview_print_descriptions"
                                   class="mt-1 h-4 w-4 shrink-0 cursor-pointer border-slate-300 text-emerald-600 focus:ring-emerald-500 dark:border-slate-600 dark:bg-slate-950">
                            <span class="min-w-0">
                                <span class="block text-sm font-semibold text-slate-900 dark:text-white">Include descriptions</span>
                                <span class="mt-1 block text-sm leading-5 text-slate-500 dark:text-slate-400">Print the overview, goal, structure, equipment, and descriptions throughout the plan.</span>
                            </span>
                        </label>

                        <label :class="[
                                   'flex cursor-pointer items-start gap-3 rounded-2xl border p-4 transition',
                                   !printDescriptions
                                       ? 'border-emerald-400 bg-emerald-50 ring-1 ring-emerald-200 dark:border-emerald-500/70 dark:bg-emerald-500/10 dark:ring-emerald-500/20'
                                       : 'border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/50 dark:border-slate-700 dark:hover:border-emerald-500/50 dark:hover:bg-emerald-500/10'
                               ]">
                            <input v-model="printDescriptions"
                                   :value="false"
                                   type="radio"
                                   name="training_plan_quick_preview_print_descriptions"
                                   class="mt-1 h-4 w-4 shrink-0 cursor-pointer border-slate-300 text-emerald-600 focus:ring-emerald-500 dark:border-slate-600 dark:bg-slate-950">
                            <span class="min-w-0">
                                <span class="block text-sm font-semibold text-slate-900 dark:text-white">Exercises only</span>
                                <span class="mt-1 block text-sm leading-5 text-slate-500 dark:text-slate-400">Print a more compact plan without descriptions or notes.</span>
                            </span>
                        </label>
                    </fieldset>

                    <div class="flex flex-wrap justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-4 dark:border-slate-800 dark:bg-slate-950/50 sm:px-6">
                        <button type="button"
                                :class="buttonClasses"
                                @click="closePrintOptions">
                            Cancel
                        </button>
                        <button type="button"
                                class="inline-flex cursor-pointer items-center justify-center gap-2 rounded-2xl border border-slate-900 bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:border-slate-700 hover:bg-slate-700 focus:outline-hidden focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 dark:border-white dark:bg-white dark:text-slate-900 dark:hover:border-slate-200 dark:hover:bg-slate-200 dark:focus:ring-offset-slate-900"
                                @click="confirmPrint"
                                data-admin2026-training-plan-quick-preview-print-confirm="true">
                            <i class="fa-solid fa-print text-sm" aria-hidden="true"></i>
                            Print plan
                        </button>
                    </div>
                </section>
            </div>
        </div>
    </Transition>
</template>

<script>
import TrainingPlanQuickPreviewCheckpoint from './TrainingPlanQuickPreviewCheckpoint.vue';
import TrainingPlanQuickPreviewNote from './TrainingPlanQuickPreviewNote.vue';

export default {
    name: 'TrainingPlanQuickPreview',
    components: {
        TrainingPlanQuickPreviewCheckpoint,
        TrainingPlanQuickPreviewNote,
    },
    emits: ['close'],
    props: {
        open: {
            type: Boolean,
            default: false,
        },
        workouts: {
            type: Array,
            default: () => [],
        },
        workoutcycles: {
            type: Array,
            default: () => [],
        },
        performanceEnabled: {
            type: Boolean,
            default: false,
        },
        performanceSchedules: {
            type: Array,
            default: () => [],
        },
        cycleLengths: {
            type: [Object, Array],
            default: () => ({}),
        },
        cyclePurposes: {
            type: [Object, Array],
            default: () => ({}),
        },
        checkpoints: {
            type: Array,
            default: () => [],
        },
        checkpointTypes: {
            type: Object,
            default: () => ({}),
        },
        placementOptions: {
            type: Object,
            default: () => ({}),
        },
        responseTypes: {
            type: Object,
            default: () => ({}),
        },
        planContent: {
            type: String,
            default: '',
        },
        planNameFallback: {
            type: String,
            default: 'Training plan',
        },
        ensureWorkoutItemsLoadedFor: {
            type: Function,
            required: true,
        },
        workoutItemsLoaded: {
            type: Function,
            required: true,
        },
        workoutItemsLoading: {
            type: Function,
            required: true,
        },
        workoutItemsError: {
            type: Function,
            required: true,
        },
        workoutExerciseCount: {
            type: Function,
            required: true,
        },
    },
    data()
    {
        return {
            loading: false,
            loadError: '',
            printOptionsOpen: false,
            printDescriptions: true,
            buttonClasses: 'inline-flex cursor-pointer items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 focus:outline-hidden focus:ring-2 focus:ring-emerald-100 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:border-slate-600 dark:hover:bg-slate-800 dark:hover:text-white dark:focus:ring-emerald-500/20 dark:focus:ring-offset-slate-900',
            iconButtonClasses: 'inline-flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-500 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 focus:outline-hidden focus:ring-2 focus:ring-emerald-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:hover:border-slate-600 dark:hover:bg-slate-800 dark:hover:text-white dark:focus:ring-emerald-500/20',
        };
    },
    computed: {
        planName()
        {
            return this.formFieldValue('name', this.planNameFallback) || 'Training plan';
        },
        planDetails()
        {
            return [
                { key: 'content', label: 'Overview', value: this.planContent },
                { key: 'goal', label: 'Goal', value: this.formFieldValue('goal') },
                { key: 'structure', label: 'Structure', value: this.formFieldValue('structure') },
                { key: 'equipment', label: 'Equipment', value: this.formFieldValue('equipment') },
            ].filter(detail => this.plainText(detail.value) !== '');
        },
        groups()
        {
            let nextDay = 1;
            const groups = this.sortedCycles.map(cycle => {
                const lengthDays = this.cycleLengthDays(cycle.id);
                const startDay = nextDay;
                const endDay = startDay + lengthDays - 1;
                nextDay = endDay + 1;

                return {
                    id: cycle.id,
                    name: cycle.name || 'Unnamed cycle',
                    content: cycle.content || '',
                    cycle_focus: cycle.cycle_focus || '',
                    purpose: String(this.cyclePurposes[String(cycle.id)] || '').trim(),
                    length_days: lengthDays,
                    start_day: startDay,
                    end_day: endDay,
                    workouts: this.cycleWorkouts(cycle.id),
                    unassigned: false,
                };
            });
            const unassignedWorkouts = this.cycleWorkouts(0);

            if (unassignedWorkouts.length)
            {
                groups.push({
                    id: 0,
                    name: 'Unassigned workouts',
                    content: '',
                    cycle_focus: '',
                    purpose: '',
                    length_days: 0,
                    start_day: 0,
                    end_day: 0,
                    workouts: unassignedWorkouts,
                    unassigned: true,
                });
            }

            return groups;
        },
        scheduleSummaries()
        {
            return (this.performanceSchedules || []).map((schedule, scheduleIndex) => {
                const workoutIds = Array.isArray(schedule.workout_ids)
                    ? schedule.workout_ids.map(workoutId => Number(workoutId))
                    : [];

                return {
                    key: schedule.key || 'schedule-' + scheduleIndex,
                    label: String(schedule.label || '').trim() || 'Schedule ' + (scheduleIndex + 1),
                    content: schedule.content || '',
                    workouts: workoutIds
                        .map(workoutId => this.workouts.find(workout => Number(workout.id) === workoutId))
                        .filter(Boolean),
                };
            });
        },
        timelineDurationDays()
        {
            return this.sortedCycles.reduce((days, cycle) => days + this.cycleLengthDays(cycle.id), 0);
        },
        planStartCheckpoints()
        {
            return this.checkpointsForPlacement('plan_start');
        },
        planEndCheckpoints()
        {
            return this.checkpointsForPlacement('plan_end');
        },
        unplacedCheckpoints()
        {
            const cycleIds = new Set(this.workoutcycles.map(cycle => Number(cycle.id)));
            const workoutIds = new Set(this.workouts.map(workout => Number(workout.id)));

            return (this.checkpoints || []).filter(checkpoint => {
                if (['plan_start', 'plan_end'].includes(checkpoint.placement_type)) return false;
                if (['cycle_start', 'cycle_end'].includes(checkpoint.placement_type))
                {
                    return !cycleIds.has(Number(checkpoint.workoutcycle_id));
                }
                if (checkpoint.placement_type === 'after_workout')
                {
                    return !workoutIds.has(Number(checkpoint.workout_id));
                }

                return true;
            });
        },
        sortedCycles()
        {
            return [...this.workoutcycles].sort((a, b) => {
                const order = parseInt(a.order || 0, 10) - parseInt(b.order || 0, 10);
                return order !== 0 ? order : String(a.name || '').localeCompare(String(b.name || ''));
            });
        },
        exerciseTotal()
        {
            return this.workouts.reduce((count, workout) => count + this.exerciseCount(workout), 0);
        },
        canPrint()
        {
            return !this.loading
                && !this.loadError
                && this.workouts.every(workout => this.workoutItemsLoaded(workout));
        },
    },
    watch: {
        open(isOpen)
        {
            if (!isOpen)
            {
                this.printOptionsOpen = false;
                return;
            }

            this.$nextTick(() => {
                window.setTimeout(() => {
                    if (this.$refs.drawer)
                    {
                        this.$refs.drawer.focus();
                    }
                }, 40);
            });
            this.load();
        },
    },
    methods: {
        close()
        {
            this.printOptionsOpen = false;
            this.$emit('close');
        },
        handleEscape()
        {
            if (this.printOptionsOpen)
            {
                this.closePrintOptions();
                return;
            }

            this.close();
        },
        openPrintOptions()
        {
            if (!this.canPrint)
            {
                return;
            }

            this.printDescriptions = true;
            this.printOptionsOpen = true;
            this.$nextTick(() => {
                if (this.$refs.printOptionsDialog)
                {
                    this.$refs.printOptionsDialog.focus();
                }
            });
        },
        closePrintOptions()
        {
            this.printOptionsOpen = false;
            this.$nextTick(() => {
                if (this.$refs.printButton)
                {
                    this.$refs.printButton.focus();
                }
            });
        },
        confirmPrint()
        {
            const includeDescriptions = this.printDescriptions;
            this.printOptionsOpen = false;
            this.print(includeDescriptions);
        },
        async load()
        {
            const workoutsToLoad = this.workouts.filter(workout => !this.workoutItemsLoaded(workout));
            this.loadError = '';

            if (!workoutsToLoad.length)
            {
                this.loading = false;
                return true;
            }

            this.loading = true;
            const loaded = await this.ensureWorkoutItemsLoadedFor(workoutsToLoad);
            this.loading = false;

            if (!loaded)
            {
                this.loadError = 'Some exercises could not be loaded. Try again before printing the plan.';
            }

            return loaded;
        },
        formFieldValue(name, fallbackValue = '')
        {
            const form = document.getElementById('admin2026-content-resource-form');
            const field = form ? form.querySelector('[name="' + name + '"]') : null;

            return field && typeof field.value !== 'undefined'
                ? field.value
                : (fallbackValue || '');
        },
        plainText(value)
        {
            if (value === null || typeof value === 'undefined')
            {
                return '';
            }

            const withLineBreaks = String(value)
                .replace(/<br\s*\/?>/gi, '\n')
                .replace(/<\/(p|div|li|h[1-6])>/gi, '\n');
            const container = document.createElement('div');
            container.innerHTML = withLineBreaks;

            return (container.textContent || container.innerText || '')
                .replace(/\u00a0/g, ' ')
                .split(/\r?\n/)
                .map(line => line.replace(/\s+/g, ' ').trim())
                .filter(line => line !== '')
                .join('\n');
        },
        cycleWorkouts(cycleId)
        {
            return [...this.workouts]
                .filter(workout => String(workout.workoutcycle_id || 0) === String(cycleId || 0))
                .sort((a, b) => {
                    const order = parseInt(a.order || 0, 10) - parseInt(b.order || 0, 10);
                    return order !== 0 ? order : parseInt(a.day || 0, 10) - parseInt(b.day || 0, 10);
                });
        },
        cycleLengthDays(cycleId)
        {
            const days = parseInt(this.cycleLengths[String(cycleId)] || 7, 10) || 7;
            return Math.max(days, 1);
        },
        timelineRange(group)
        {
            if (!group || group.unassigned) return '';

            const range = group.start_day === group.end_day
                ? 'Day ' + group.start_day
                : 'Days ' + group.start_day + '–' + group.end_day;

            return range + ' · ' + group.length_days + ' ' + (group.length_days === 1 ? 'day' : 'days');
        },
        scheduleLabelsForWorkout(workoutId)
        {
            return this.scheduleSummaries
                .filter(schedule => schedule.workouts.some(workout => Number(workout.id) === Number(workoutId)))
                .map(schedule => schedule.label);
        },
        checkpointsForPlacement(placementType)
        {
            return (this.checkpoints || []).filter(checkpoint => checkpoint.placement_type === placementType);
        },
        cycleCheckpoints(cycleId, placementType)
        {
            return (this.checkpoints || []).filter(checkpoint => checkpoint.placement_type === placementType
                && Number(checkpoint.workoutcycle_id) === Number(cycleId));
        },
        workoutCheckpoints(workoutId)
        {
            return (this.checkpoints || []).filter(checkpoint => checkpoint.placement_type === 'after_workout'
                && Number(checkpoint.workout_id) === Number(workoutId));
        },
        checkpointTypeLabel(checkpoint)
        {
            return this.checkpointTypes[String((checkpoint && checkpoint.type) || '')]
                || (checkpoint && checkpoint.type === 'questionnaire' ? 'Questionnaire' : 'Test');
        },
        checkpointTimingLabel(checkpoint)
        {
            if (!checkpoint) return 'Checkpoint';

            if (checkpoint.placement_type === 'cycle_start' || checkpoint.placement_type === 'cycle_end')
            {
                const cycle = this.workoutcycles.find(item => Number(item.id) === Number(checkpoint.workoutcycle_id));
                const position = checkpoint.placement_type === 'cycle_start' ? 'At the start of ' : 'At the end of ';
                return position + String((cycle && cycle.name) || 'the cycle');
            }
            if (checkpoint.placement_type === 'after_workout')
            {
                const workout = this.workouts.find(item => Number(item.id) === Number(checkpoint.workout_id));
                return 'After ' + String((workout && workout.name) || 'the workout');
            }

            return this.placementOptions[String(checkpoint.placement_type || '')] || 'Checkpoint';
        },
        exerciseCount(workout)
        {
            return this.workoutExerciseCount(workout);
        },
        workoutLabel(workout, workoutIndex)
        {
            const name = String((workout && workout.name) || '').trim();
            return name || 'Workout ' + (workoutIndex + 1);
        },
        workoutDay(workout)
        {
            const day = parseInt((workout && workout.day) || 0, 10) || 0;
            return day > 0 ? 'Day ' + day : '';
        },
        duration(value, minutes = 0, seconds = 0)
        {
            if (String(value || '').includes(':'))
            {
                const parts = String(value).split(':').map(part => parseInt(part || 0, 10) || 0);
                while (parts.length > 2 && parts[0] === 0)
                {
                    parts.shift();
                }

                return parts.map((part, index) => index === 0 ? String(part) : String(part).padStart(2, '0')).join(':');
            }

            const hours = parseInt(value || 0, 10) || 0;
            const mins = parseInt(minutes || 0, 10) || 0;
            const secs = parseInt(seconds || 0, 10) || 0;

            if (hours > 0)
            {
                return hours + ':' + String(mins).padStart(2, '0') + ':' + String(secs).padStart(2, '0');
            }

            if (mins > 0)
            {
                return mins + ':' + String(secs).padStart(2, '0');
            }

            return secs > 0 ? secs + 's' : '';
        },
        variableValue(variable)
        {
            if (!variable)
            {
                return '';
            }

            const type = parseInt(variable.type || 0, 10) || 0;
            const unit = String(variable.measurement_unit || ((type === 4 || type === 6) ? variable.value2 : '') || '').trim();

            if (variable.range)
            {
                const rawRangeFrom = String(variable.value_range_from ?? '').trim();
                const rawRangeTo = String(variable.value_range_to ?? '').trim();

                if (rawRangeFrom !== '' || rawRangeTo !== '')
                {
                    const rangeFrom = this.variableRangeBound(variable, rawRangeFrom);
                    const rangeTo = this.variableRangeBound(variable, rawRangeTo);
                    const rangeValue = rangeFrom !== '' && rangeTo !== ''
                        ? (rawRangeFrom === rawRangeTo ? rangeFrom : rangeFrom + '–' + rangeTo)
                        : (rangeFrom || rangeTo);

                    return this.variableValueWithUnit(rangeValue, unit);
                }
            }

            if (type === 3)
            {
                return this.duration(variable.value, variable.value2, variable.value3);
            }

            const value = String(variable.value ?? '').trim();
            if (value === '' || (!isNaN(value) && Number(value) === 0))
            {
                return '';
            }

            return this.variableValueWithUnit(value, unit);
        },
        variableRangeBound(variable, value)
        {
            const rangeValue = String(value || '').trim();
            const isDuration = parseInt((variable || {}).type || 0, 10) === 3
                && String((variable || {}).name || '').trim().toLowerCase() === 'duration';

            if (!isDuration || !/^\d+$/.test(rangeValue))
            {
                return rangeValue;
            }

            const totalSeconds = parseInt(rangeValue, 10) || 0;
            const hours = Math.floor(totalSeconds / 3600);
            const minutes = Math.floor((totalSeconds % 3600) / 60);
            const seconds = totalSeconds % 60;

            return this.duration(hours, minutes, seconds);
        },
        variableValueWithUnit(value, unit)
        {
            const displayValue = String(value || '').trim();
            const displayUnit = String(unit || '').trim();

            if (!displayValue || !displayUnit)
            {
                return displayValue;
            }

            const escapedUnit = displayUnit.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            if (new RegExp('(?:^|\\s)' + escapedUnit + '$', 'i').test(displayValue))
            {
                return displayValue;
            }

            return displayValue + ' ' + displayUnit;
        },
        variableDuplicatesSetsColumn(item, variable)
        {
            const sets = parseInt((item || {}).sets || 0, 10) || 0;
            if (sets <= 0 || String((variable || {}).name || '').trim().toLowerCase() !== 'sets')
            {
                return false;
            }

            const value = this.variableValue(variable);
            return /^\d+(?:\.0+)?$/.test(value) && Number(value) === sets;
        },
        variableDisplay(variable)
        {
            const value = this.variableValue(variable);
            const failure = variable && variable.to_failure ? 'to failure' : '';
            if (!value && !failure)
            {
                return '';
            }

            const template = String((variable || {}).abbreviation || (variable || {}).name || '').trim();
            const display = template.includes('PONO:value')
                ? template.split('PONO:value').join(value)
                : [template, value].filter(part => part !== '').join(' ');

            return [display.replace(/\s+/g, ' ').trim(), failure]
                .filter(part => part !== '')
                .join(' ');
        },
        variableSummary(item)
        {
            const variables = Array.isArray((item || {}).trainingvariables)
                ? [...item.trainingvariables]
                : [];

            return variables
                .filter(variable => variable && variable.visible != 0)
                .filter(variable => !this.variableDuplicatesSetsColumn(item, variable))
                .sort((a, b) => {
                    const order = parseInt(a.order || 0, 10) - parseInt(b.order || 0, 10);
                    return order !== 0 ? order : String(a.name || '').localeCompare(String(b.name || ''));
                })
                .map(variable => this.variableDisplay(variable))
                .filter(value => value !== '')
                .join(' · ');
        },
        print(includeDescriptions = true)
        {
            if (!this.canPrint || !this.$refs.printSurface)
            {
                return;
            }

            const printCopy = this.$refs.printSurface.cloneNode(true);
            printCopy.setAttribute('data-admin2026-training-plan-quick-preview-print-copy', 'true');

            if (!includeDescriptions)
            {
                printCopy.setAttribute('data-admin2026-training-plan-quick-preview-exercises-only', 'true');
                printCopy.querySelectorAll('[data-admin2026-training-plan-quick-preview-description="true"], [data-admin2026-training-plan-quick-preview-notes-column="true"]')
                    .forEach(element => element.remove());
                printCopy.querySelectorAll('[data-admin2026-training-plan-quick-preview-section="true"]')
                    .forEach(section => {
                        if (!section.querySelector('[data-admin2026-training-plan-quick-preview-section-name="true"]'))
                        {
                            section.remove();
                            return;
                        }

                        const cell = section.querySelector('td[colspan]');
                        if (cell)
                        {
                            cell.setAttribute('colspan', '4');
                        }
                    });
                printCopy.querySelectorAll('[data-admin2026-training-plan-quick-preview-exercise-table="true"]')
                    .forEach(table => {
                        const columns = table.querySelectorAll('col');
                        if (columns[1])
                        {
                            columns[1].style.width = '42%';
                        }
                        if (columns[3])
                        {
                            columns[3].style.width = 'auto';
                        }
                    });
            }

            document.body.appendChild(printCopy);
            document.body.classList.add('admin2026-training-plan-quick-preview-printing');

            let cleanedUp = false;
            const cleanup = () => {
                if (cleanedUp)
                {
                    return;
                }

                cleanedUp = true;
                document.body.classList.remove('admin2026-training-plan-quick-preview-printing');
                printCopy.remove();
            };

            window.addEventListener('afterprint', cleanup, { once: true });
            window.setTimeout(cleanup, 60000);
            window.print();
        },
    },
};
</script>
